import React, {FC, memo} from "react";
import {Button, Modal, Popconfirm, Space, Tag, Tooltip} from "antd";
import {DeleteFilled, PlusCircleOutlined} from "@ant-design/icons";
import {
    DiscountUnit,
    GetBookingByIdDocument,
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetBookingByIdWithCourseLessonBookingsQuery,
    GetPaidScheduledPaymentsDocument,
    GetUnpaidScheduledPaymentsDocument,
    useGetCourseMemberDiscountQuery,
    useUpdateSettledBookingDiscountMutation,
} from "../../generated/graphql";
import {RenderAmount} from "../TableColumnRenderers/TableColumnRenderers";
import MemberDiscount from "./MemberDiscount";
import {useCurrency} from "../../helpers/currencyHelper";
import {renderAmount, CurrencyShort} from "../../helpers/renderAmount";
import {erasedRecordMessage} from "../../helpers/erasedRecordMessage";

type PossibleBookingType =
    | GetBookingByIdQuery["bookingById"]
    | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type SettledBookingMemberDiscountProps = {
    booking?: PossibleBookingType;
    bookingId: string;
    allPaid: boolean;
    setTimeStamp?: (ts: number) => void;
};

const SettledBookingMemberDiscount: FC<SettledBookingMemberDiscountProps> = ({booking, bookingId, allPaid, setTimeStamp}) => {
    const currency = useCurrency();
    const memberDiscount = booking?.memberDiscount ?? 0;
    const courseId = booking?.courseId;
    const isMember = booking?.attendee?.member;

    // Lade Kurs-Mitgliederrabatt
    const {data: courseMemberDiscountData} = useGetCourseMemberDiscountQuery({
        variables: {id: courseId ?? ""},
        skip: !courseId || allPaid,
    });

    const [updateDiscount, {loading}] = useUpdateSettledBookingDiscountMutation({
        refetchQueries: [
            {query: GetBookingByIdDocument, variables: {id: bookingId}},
            {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: bookingId}},
            {query: GetUnpaidScheduledPaymentsDocument, variables: {bookingId, options: {}}},
            {query: GetPaidScheduledPaymentsDocument, variables: {bookingId, options: {}}},
        ],
        awaitRefetchQueries: true,
    });

    // Wenn alle Posten bezahlt -> read-only Fallback
    if (allPaid) {
        return <MemberDiscount booking={booking} />;
    }

    const hasPartiallyPaidOpenSP = (booking?.scheduledPayments ?? []).some((sp) => {
        if (sp.paymentDone != null || sp.openAmount == null || sp.paymentAmount == null) {
            return false;
        }
        const openAmount = Number(sp.openAmount);
        const paymentAmount = Number(sp.paymentAmount);

        return openAmount > 0 && openAmount < paymentAmount;
    });

    const courseDiscount = courseMemberDiscountData?.courseById?.coursePrice?.[0]?.discount;
    const courseDiscountUnit = courseMemberDiscountData?.courseById?.coursePrice?.[0]?.discountUnit;

    // Berechne den Mitgliederrabatt-Betrag
    const calculateMemberDiscountAmount = (): number => {
        if (!courseDiscount || courseDiscount === 0) {
            return 0;
        }

        if (courseDiscountUnit === DiscountUnit.Euro) {
            return courseDiscount;
        }

        // Prozent: Basierend auf grossPrice
        const grossPrice = booking?.grossPrice ?? 0;

        return Math.round(((grossPrice * courseDiscount) / 100) * 100) / 100;
    };

    const showCorrectionInvoiceHint = () => {
        Modal.info({
            title: "Hinweis: Korrekturrechnung",
            content: "Die Buchungsbeträge wurden angepasst. Bitte prüfe, ob eine Korrekturrechnung erstellt und dem Bucher zugeschickt werden muss, damit dieser eine aktuelle Rechnung erhält.",
        });
    };

    const handleApplyMemberDiscount = async () => {
        const amount = calculateMemberDiscountAmount();

        if (amount <= 0) {
            return;
        }

        try {
            await updateDiscount({
                variables: {
                    bookingId,
                    memberDiscount: amount,
                },
            });
            setTimeStamp?.(Date.now());
            showCorrectionInvoiceHint();
        } catch (error) {
            Modal.error({
                title: "Fehler",
                content: erasedRecordMessage(error) ?? "Der Mitgliederrabatt konnte nicht angewendet werden.",
            });
        }
    };

    const handleRemoveMemberDiscount = async () => {
        try {
            await updateDiscount({
                variables: {
                    bookingId,
                    memberDiscount: 0,
                },
            });
            setTimeStamp?.(Date.now());
            showCorrectionInvoiceHint();
        } catch (error) {
            Modal.error({
                title: "Fehler",
                content: erasedRecordMessage(error) ?? "Der Mitgliederrabatt konnte nicht entfernt werden.",
            });
        }
    };

    // Mitgliederrabatt vorhanden -> Badge mit Delete
    if (memberDiscount > 0) {
        return (
            <Tag color="blue">
                <Space size={5}>
                    <span>{"Verrechneter Mitgliederrabatt von"}</span>
                    <RenderAmount amount={memberDiscount} style={{fontSize: "unset", fontWeight: 600}} />
                    {hasPartiallyPaidOpenSP ? (
                        <Tooltip title="Teilbezahlte offene Raten — Mitgliederrabatt kann nicht geändert werden.">
                            <DeleteFilled style={{color: "#d9d9d9", cursor: "not-allowed"}} />
                        </Tooltip>
                    ) : (
                        <Popconfirm
                            title="Mitgliederrabatt wirklich entfernen?"
                            onConfirm={handleRemoveMemberDiscount}
                            okText="Ja, entfernen"
                            okType="danger"
                            cancelText="Abbrechen"
                        >
                            <Tooltip title="Mitgliederrabatt entfernen">
                                <DeleteFilled style={{cursor: "pointer", color: "#ff4d4f"}} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            </Tag>
        );
    }

    // Kein Mitgliederrabatt, aber Attendee ist Mitglied und Kurs hat Rabatt
    if (isMember && courseDiscount && courseDiscount > 0) {
        const discountAmount = calculateMemberDiscountAmount();
        const discountLabel = courseDiscountUnit === DiscountUnit.Euro
            ? renderAmount({amount: discountAmount, currency: currency.short as CurrencyShort, short: true})
            : `${courseDiscount}%`;

        if (hasPartiallyPaidOpenSP) {
            return (
                <Tooltip title="Teilbezahlte offene Raten können nicht automatisch rabattiert werden — bitte die Teilzahlung zuerst abschließen.">
                    <Button type="dashed" size="small" icon={<PlusCircleOutlined />} disabled>
                        {`Mitgliederrabatt anwenden (${discountLabel})`}
                    </Button>
                </Tooltip>
            );
        }

        return (
            <Button
                type="dashed"
                size="small"
                icon={<PlusCircleOutlined />}
                onClick={handleApplyMemberDiscount}
                loading={loading}
            >
                {`Mitgliederrabatt anwenden (${discountLabel})`}
            </Button>
        );
    }

    // Kein Mitglied oder kein Kurs-Rabatt -> nichts anzeigen
    return null;
};

export default memo(SettledBookingMemberDiscount);
